import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Stethoscope, FilePdf, Envelope, FileCsv, CaretRight,
  Question, Bug, Info, ShieldCheck, FileText, SignOut, Trash,
} from "@phosphor-icons/react";
import UpgradePanel from "@/components/UpgradePanel";
import AddToHomeButton from "@/components/AddToHomeButton";
import ShareButton from "@/components/ShareButton";
import { usePlanUsage } from "@/lib/plan";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const SUPPORT_EMAIL = "tallycarbcounter@gmail.com";

export default function Profile() {
  const { user, setUser, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { usage, refresh: refreshUsage } = usePlanUsage();
  const upgradeRef = useRef(null);

  const [name, setName] = useState(user?.name || "");
  const [model, setModel] = useState(user?.preferred_model || "gemini-3-flash-preview");
  const [doctorName, setDoctorName] = useState(user?.doctor_name || "");
  const [doctorEmail, setDoctorEmail] = useState(user?.doctor_email || "");
  const [saving, setSaving] = useState(false);
  const [pollingStatus, setPollingStatus] = useState(null);

  const isAdvanced = usage?.plan?.tier === "advanced";

  useEffect(() => {
    if (params.get("focus") === "upgrade" && upgradeRef.current) {
      upgradeRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [params, usage]);

  // Handle Stripe redirect (?upgrade=success&session_id=...)
  useEffect(() => {
    const upgrade = params.get("upgrade");
    const sid = params.get("session_id");

    if (upgrade === "cancel") {
      toast.info("Upgrade canceled.");
      const next = new URLSearchParams(params);
      next.delete("upgrade");
      setParams(next, { replace: true });
      return;
    }

    if (upgrade === "success" && sid) {
      setPollingStatus("Confirming your payment…");
      let attempts = 0;
      const max = 8;
      const tick = async () => {
        try {
          const { data } = await api.get(`/plans/status/${sid}`);
          if (data.payment_status === "paid") {
            setPollingStatus(null);
            toast.success("Welcome to Advanced!");
            await refresh();
            await refreshUsage();
            const next = new URLSearchParams(params);
            next.delete("upgrade");
            next.delete("session_id");
            setParams(next, { replace: true });
            return;
          }
          if (data.status === "expired" || attempts >= max) {
            setPollingStatus(null);
            toast.error("Payment not completed. You can try again.");
            const next = new URLSearchParams(params);
            next.delete("upgrade");
            next.delete("session_id");
            setParams(next, { replace: true });
            return;
          }
          attempts += 1;
          setTimeout(tick, 2000);
        } catch (e) {
          setPollingStatus(null);
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not verify payment");
        }
      };
      tick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", {
        name,
        preferred_model: model,
        doctor_name: doctorName || null,
        doctor_email: doctorEmail || null,
      });
      setUser(data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get("/reports/weekly.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tally-weekly-report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not generate report");
    }
  };

  const downloadCsv = async () => {
    try {
      const r = await api.get("/meals/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "tally-meals.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not export CSV");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const [deleting, setDeleting] = useState(false);
  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/auth/account");
      toast.success("Account deleted");
      await logout();
      navigate("/login", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete account");
    } finally {
      setDeleting(false);
    }
  };

  const emailDoctor = async () => {
    if (!doctorEmail) {
      toast.info("Add your doctor's email first.");
      return;
    }
    // First trigger the PDF download so user has it ready to attach
    await downloadPdf();
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - 6);
    const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    const period = `${fmt(start)} – ${fmt(today)}`;
    const patient = user?.name || user?.email?.split("@")[0] || "Patient";
    const greeting = doctorName ? `Dr. ${doctorName}` : "Doctor";
    const subject = `Tally · Weekly Carb Report for ${patient} (${period})`;
    const body =
      `Hello ${greeting},\n\n` +
      `Please find my weekly carb report attached (${period}).\n` +
      `The PDF was just saved to my device — I've attached it to this email.\n\n` +
      `Thank you,\n${patient}`;
    const href = `mailto:${encodeURIComponent(doctorEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    // small delay so the download initiates first
    setTimeout(() => { window.location.href = href; }, 250);
  };

  return (
    <div className="px-5 pt-4 pb-2">
      <span className="label-eyebrow">Account · Preferences</span>
      <h1 className="font-serif-display text-3xl tracking-tight text-[#1A2421] mt-1">Your settings</h1>

      {pollingStatus && (
        <div className="mt-4 rounded-2xl bg-[#2B422F] text-white px-4 py-3 text-sm" data-testid="payment-polling-status">
          {pollingStatus}
        </div>
      )}

      {/* Plan / Upgrade */}
      <div ref={upgradeRef} className="mt-6">
        <UpgradePanel usage={usage} onUpdated={refreshUsage} />
      </div>

      {/* Weekly report */}
      <div className="card-organic p-5 mt-6 space-y-4" data-testid="report-card">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full bg-[#2B422F] flex items-center justify-center">
            <Stethoscope size={18} weight="regular" className="text-[#E6D4C4]" />
          </span>
          <div>
            <div className="font-serif-display text-xl text-[#1A2421] leading-none">Weekly Doctor Report</div>
            <div className="text-xs text-[#5C6B62] mt-1">Last 7 days of meals, formatted for your care team.</div>
          </div>
        </div>

        <div>
          <label className="label-eyebrow block mb-2">Doctor&apos;s name (optional)</label>
          <input
            className="input-organic"
            value={doctorName}
            onChange={(e) => setDoctorName(e.target.value)}
            placeholder="e.g. Sarah Lee"
            data-testid="doctor-name-input"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Doctor&apos;s email (optional)</label>
          <input
            type="email"
            className="input-organic"
            value={doctorEmail}
            onChange={(e) => setDoctorEmail(e.target.value)}
            placeholder="dr@clinic.com"
            data-testid="doctor-email-input"
          />
          <p className="text-xs text-[#5C6B62] mt-2">Used in the report header. You can forward the PDF to this address.</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={downloadPdf}
            className="btn-outline flex items-center justify-center gap-2"
            data-testid="download-pdf-button"
          >
            <FilePdf size={16} weight="regular" /> Download
          </button>
          <button
            onClick={emailDoctor}
            disabled={!doctorEmail}
            className="btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="email-doctor-button"
            title={!doctorEmail ? "Add a doctor email first" : undefined}
          >
            <Envelope size={16} weight="regular" /> Email doctor
          </button>
        </div>
        <p className="text-[11px] text-[#5C6B62] -mt-1">
          {doctorEmail
            ? "Tapping Email doctor saves the PDF and opens your email app — just attach the PDF and send."
            : "Add your doctor's email above to enable one-tap email."}
        </p>
        {isAdvanced && (
          <button
            onClick={downloadCsv}
            className="btn-outline w-full flex items-center justify-center gap-2"
            data-testid="download-csv-button"
          >
            <FileCsv size={16} /> Export all meals (CSV)
          </button>
        )}
      </div>

      {/* Account form */}
      <div className="card-organic p-5 mt-6 space-y-5" data-testid="profile-form">
        <div>
          <label className="label-eyebrow block mb-2">Name</label>
          <input className="input-organic" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Email</label>
          <input className="input-organic !bg-[#F9F8F6]" value={user?.email || ""} disabled data-testid="profile-email-input" />
        </div>

        <div>
          <label className="label-eyebrow block mb-3">AI Model Preference</label>
          <RadioGroup value={model} onValueChange={setModel} className="grid grid-cols-1 gap-2.5" data-testid="profile-model-group">
            {[
              { v: "gemini-3-flash-preview", t: "Gemini 3 Flash", d: "Faster, lower cost.", advancedOnly: false },
              { v: "gpt-4o", t: "GPT-4o Vision", d: "More detailed reasoning.", advancedOnly: true },
            ].map((o) => {
              const locked = o.advancedOnly && !isAdvanced;
              return (
                <label
                  key={o.v}
                  className={`flex items-start gap-3 rounded-2xl border p-4 transition-colors ${
                    locked ? "border-[#E6E3D8] bg-[#F9F8F6] opacity-70 cursor-not-allowed" :
                    model === o.v ? "border-[#2B422F] bg-[#F0EFE9] cursor-pointer" : "border-[#E6E3D8] bg-white hover:bg-[#F9F8F6] cursor-pointer"
                  }`}
                  data-testid={`profile-model-option-${o.v}`}
                >
                  <RadioGroupItem value={o.v} id={o.v} className="mt-1" disabled={locked} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[#1A2421]">{o.t}</span>
                      {o.advancedOnly && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#C76E4B] text-white">Advanced</span>
                      )}
                    </div>
                    <div className="text-xs text-[#5C6B62] mt-0.5">{o.d}</div>
                  </div>
                </label>
              );
            })}
          </RadioGroup>
        </div>

        <div className="pt-2">
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50" data-testid="profile-save-button">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {/* Settings & Support */}
      <div className="card-organic mt-6 overflow-hidden" data-testid="settings-menu">
        <a
          href={`mailto:${SUPPORT_EMAIL}?subject=Tally%20%C2%B7%20Help`}
          className="flex items-center justify-between gap-3 px-5 py-4 hover:bg-[#F0EFE9] transition-colors"
          data-testid="menu-help"
        >
          <div className="flex items-center gap-3">
            <Question size={18} className="text-[#5C6B62]" />
            <span className="text-sm text-[#1A2421]">Help &amp; Contact Support</span>
          </div>
          <CaretRight size={14} className="text-[#5C6B62]" />
        </a>
        <a
          href={`mailto:${SUPPORT_EMAIL}?subject=Tally%20%C2%B7%20Report%20a%20Problem&body=${encodeURIComponent("Describe the issue you ran into:\n\n\n\n— Sent from Tally")}`}
          className="flex items-center justify-between gap-3 px-5 py-4 border-t border-[#E6E3D8] hover:bg-[#F0EFE9] transition-colors"
          data-testid="menu-report"
        >
          <div className="flex items-center gap-3">
            <Bug size={18} className="text-[#5C6B62]" />
            <span className="text-sm text-[#1A2421]">Report a Problem</span>
          </div>
          <CaretRight size={14} className="text-[#5C6B62]" />
        </a>
        <button
          onClick={() => navigate("/about")}
          className="w-full flex items-center justify-between gap-3 px-5 py-4 border-t border-[#E6E3D8] hover:bg-[#F0EFE9] transition-colors text-left"
          data-testid="menu-about"
        >
          <div className="flex items-center gap-3">
            <Info size={18} className="text-[#5C6B62]" />
            <span className="text-sm text-[#1A2421]">About the App</span>
          </div>
          <CaretRight size={14} className="text-[#5C6B62]" />
        </button>
        <AddToHomeButton />
        <ShareButton />
        <button
          onClick={() => navigate("/privacy")}
          className="w-full flex items-center justify-between gap-3 px-5 py-4 border-t border-[#E6E3D8] hover:bg-[#F0EFE9] transition-colors text-left"
          data-testid="menu-privacy"
        >
          <div className="flex items-center gap-3">
            <ShieldCheck size={18} className="text-[#5C6B62]" />
            <span className="text-sm text-[#1A2421]">Privacy Policy</span>
          </div>
          <CaretRight size={14} className="text-[#5C6B62]" />
        </button>
        <button
          onClick={() => navigate("/terms")}
          className="w-full flex items-center justify-between gap-3 px-5 py-4 border-t border-[#E6E3D8] hover:bg-[#F0EFE9] transition-colors text-left"
          data-testid="menu-terms"
        >
          <div className="flex items-center gap-3">
            <FileText size={18} className="text-[#5C6B62]" />
            <span className="text-sm text-[#1A2421]">Terms of Service</span>
          </div>
          <CaretRight size={14} className="text-[#5C6B62]" />
        </button>
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-between gap-3 px-5 py-4 border-t border-[#E6E3D8] hover:bg-[#F0EFE9] transition-colors text-left"
          data-testid="menu-logout"
        >
          <div className="flex items-center gap-3">
            <SignOut size={18} className="text-[#5C6B62]" />
            <span className="text-sm text-[#1A2421]">Log out</span>
          </div>
          <CaretRight size={14} className="text-[#5C6B62]" />
        </button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              className="w-full flex items-center justify-between gap-3 px-5 py-4 border-t border-[#E6E3D8] hover:bg-[#F9F0EC] transition-colors text-left"
              data-testid="menu-delete-account"
            >
              <div className="flex items-center gap-3">
                <Trash size={18} className="text-[#C76E4B]" />
                <span className="text-sm text-[#C76E4B] font-medium">Delete Account</span>
              </div>
              <CaretRight size={14} className="text-[#C76E4B]" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="max-w-sm rounded-2xl" data-testid="delete-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif-display text-2xl text-[#1A2421]">Delete your account?</AlertDialogTitle>
              <AlertDialogDescription className="text-[#5C6B62] text-sm leading-relaxed">
                This permanently removes your account, all saved meals, scan history, and subscription. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2">
              <AlertDialogCancel data-testid="delete-cancel">Keep account</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="bg-[#C76E4B] hover:bg-[#B35F3F] text-white"
                data-testid="delete-confirm"
              >
                {deleting ? "Deleting…" : "Yes, delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <p className="text-[10px] text-[#5C6B62]/70 text-center mt-6">Tally Carb Counter · v1.0</p>
    </div>
  );
}
